import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  estimateMicros,
  type Garment,
  type ModelWithStats,
  type Scene,
  type SceneLook,
  type ShootSettings,
} from '@studio/shared';

import { api, assetUrl } from '../lib/api';
import { useDateFormat } from '../lib/dates';
import { useErrorText } from '../lib/errors';
import { useI18n, useT, type Translate } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useSession } from '../lib/session';
import { DEFAULT_SETTINGS, defaultName } from '../lib/shootDefaults';
import { ClothesGuide } from '../components/ClothesGuide';
import { OptionCard } from '../components/OptionCard';
import { Segmented } from '../components/Segmented';
import { ArrowRightIcon, CheckIcon, SparkIcon } from '../components/Icons';
import { useToast } from '../components/Toaster';
import { ModelEditor } from './Models';
import { ApertureField, Field, ModelStep, portraitOf, RecapRow, Thumb } from './Wizard';

type Step = 'model' | 'clothes' | 'scenes' | 'outfit' | 'finish' | 'go';
const STEPS: Step[] = ['model', 'clothes', 'scenes', 'outfit', 'finish', 'go'];

/** A look picked for a scene: one of the stylist's, by its place, or the client's own words. */
type LookPick = { index: number } | { other: string };
/** The stylist's looks for a scene, while they are asked for, or why there are none. */
type Looks = SceneLook[] | 'loading' | 'failed';

/**
 * Recreate one of the Studio's scenes, set up the way a roll of poses is: one
 * question at a time — who, in what, which scenes, how the pieces are worn in
 * each, any finishing — with the count of what will be shot always at the foot.
 *
 * A scene is a finished photograph — a place, a pose, an outfit, the Studio's
 * own model. This client's model and pieces take the place of the ones in it,
 * and everything else stays exactly as it is: one photograph per scene.
 */
export function CopyShootPage() {
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const money = useMoney();
  const when = useDateFormat();
  const describe = useErrorText();
  const { user } = useSession();
  const staff = Boolean(user?.staff);

  const [step, setStep] = useState<Step>('model');
  const [models, setModels] = useState<ModelWithStats[]>([]);
  const [scenes, setScenes] = useState<Scene[] | undefined>();

  const [modelId, setModelId] = useState<string | undefined>();
  const [garments, setGarments] = useState<Garment[]>([]);
  const [sceneIds, setSceneIds] = useState<string[]>([]);
  const [settings, setSettings] = useState<ShootSettings>({ ...DEFAULT_SETTINGS });
  const [name, setName] = useState('');
  /** By scene and pieces: the stylist's looks change with the pieces, and so does what was picked from them. */
  const [looks, setLooks] = useState<Record<string, Looks>>({});
  const [picks, setPicks] = useState<Record<string, LookPick>>({});

  const [creatingModel, setCreatingModel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadModels = () =>
    api
      .listModels()
      .then((list) => {
        setModels(list);
        return list;
      })
      .catch(() => [] as ModelWithStats[]);

  useEffect(() => {
    void loadModels().then((list) => {
      if (list.length === 1 && list[0]) setModelId(list[0].id);
    });
    void api
      .listScenes()
      .then(setScenes)
      .catch(() => setScenes([]));
  }, []);

  const set = <K extends keyof ShootSettings>(key: K, value: ShootSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const chosen = sceneIds.map((id) => scenes?.find((s) => s.id === id)).filter((s): s is Scene => Boolean(s));
  const pieces = garments.map((g) => g.id).join(',');
  const lookKey = (scene: Scene) => `${scene.id}|${pieces}`;

  const askLooks = (scene: Scene) => {
    const key = lookKey(scene);
    setLooks((all) => ({ ...all, [key]: 'loading' }));
    api
      .sceneLooks(
        scene.id,
        garments.map((g) => g.id),
        locale,
      )
      .then((found) => setLooks((all) => ({ ...all, [key]: found.looks })))
      .catch(() => setLooks((all) => ({ ...all, [key]: 'failed' })));
  };

  // The stylist is asked for each scene once its pieces are known, when the step opens.
  useEffect(() => {
    if (step !== 'outfit' || !garments.length) return;
    for (const scene of chosen) if (!looks[lookKey(scene)]) askLooks(scene);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, pieces, sceneIds.join(',')]);

  /** What goes with a scene to the server: the look picked for it. */
  const lookFor = (scene: Scene): RollLook | undefined => {
    const pick = picks[lookKey(scene)];
    const offered = looks[lookKey(scene)];
    if (!pick) return undefined;
    if ('other' in pick) return { title: t('stylist.other'), other: pick.other };
    return Array.isArray(offered) ? offered[pick.index] : undefined;
  };
  // One photograph per scene: each is the scene's own photograph, with her and these pieces in it.
  const count = modelId ? chosen.length : 0;
  const engine = settings.fidelity === 'standard' ? 'standard' : 'max';
  const cost = estimateMicros(count, { fidelity: engine });
  const perPhoto = (fidelity: 'max' | 'standard') => estimateMicros(1, { fidelity });
  const breakdown = t(chosen.length === 1 ? 'scenewiz.n.scene' : 'scenewiz.n.scenes', { n: chosen.length });

  /** What each step still needs before the next, if anything. */
  const missing: Partial<Record<Step, string>> = {
    model: modelId ? undefined : t('wiz.need.model'),
    clothes: garments.length ? undefined : t('wiz.need.clothes'),
    scenes: chosen.length ? undefined : t('scenewiz.need.scenes'),
    outfit: chosen.every((scene) => lookFor(scene)) ? undefined : t('scenewiz.need.outfit'),
  };
  const index = STEPS.indexOf(step);
  const blocker = missing[step];
  /** A step can be visited when every step before it is answered. */
  const reachable = (target: Step) => STEPS.slice(0, STEPS.indexOf(target)).every((s) => !missing[s]);

  const submit = async () => {
    if (!modelId) return;
    setSubmitting(true);
    setError('');
    try {
      const shoot = await api.createShoot({
        name: name.trim() || defaultName(garments, t, when.short),
        modelIds: [modelId],
        garmentIds: garments.map((g) => g.id),
        povIds: ['full-front'],
        settings: {
          ...settings,
          mode: 'scene',
          // The server replaces these ids with its own copy of each scene.
          sceneRefs: chosen.map((s) => ({
            id: s.id,
            look: lookFor(s),
          })) as unknown as ShootSettings['sceneRefs'],
        },
        start: true,
      });
      toast(t(count === 1 ? 'shoot.rollingOne' : 'shoot.rolling', { n: count, name: shoot.name }), 'ok');
      navigate(`/shoots/${shoot.id}`);
    } catch (err) {
      setError(describe(err, 'shoot.failed'));
      setSubmitting(false);
    }
  };

  const next = () => {
    if (blocker) return;
    if (step === 'go') void submit();
    else setStep(STEPS[index + 1]!);
  };

  const model = models.find((m) => m.id === modelId);

  return (
    <div className="wiz">
      <nav className="wiz__steps" aria-label={t('copy.eyebrow')}>
        {STEPS.map((s, i) => (
          <button
            key={s}
            type="button"
            className={`wiz__step${s === step ? ' is-now' : ''}${i < index && !missing[s] ? ' is-done' : ''}`}
            aria-current={s === step ? 'step' : undefined}
            disabled={!reachable(s)}
            onClick={() => setStep(s)}
          >
            <b>{i + 1}</b>
            <span>{t(`scenewiz.step.${s}`)}</span>
          </button>
        ))}
      </nav>

      <section className="wiz__body" aria-live="polite">
        <header className="wiz__head">
          <h1 className="wiz__title">{t(`scenewiz.title.${step}`)}</h1>
          <p className="wiz__lede">{t(`scenewiz.lede.${step}`)}</p>
        </header>

        {error && <div className="banner banner--error">{error}</div>}

        {step === 'model' && (
          <ModelStep
            models={models}
            selected={modelId}
            staff={staff}
            onSelect={setModelId}
            onCreate={() => setCreatingModel(true)}
          />
        )}

        {step === 'clothes' && <ClothesGuide garments={garments} onChange={setGarments} />}

        {step === 'scenes' && (
          <div className="wiz__pane">
            <div className="wiz__grid">
              {(scenes ?? []).map((scene) => {
                const on = sceneIds.includes(scene.id);
                return (
                  <OptionCard
                    key={scene.id}
                    caption={false}
                    title={scene.name}
                    selected={on}
                    order={on ? sceneIds.indexOf(scene.id) + 1 : undefined}
                    image={assetUrl(scene.assetId)}
                    onToggle={() =>
                      setSceneIds((list) =>
                        list.includes(scene.id) ? list.filter((id) => id !== scene.id) : [...list, scene.id],
                      )
                    }
                  />
                );
              })}
              {scenes === undefined && <span className="spinner" />}
              {scenes?.length === 0 && (
                <p className="faint wiz__empty">{t(staff ? 'scenewiz.emptyStaff' : 'copy.noScenes')}</p>
              )}
            </div>
          </div>
        )}

        {step === 'outfit' && (
          <div className="wiz__pane slook__list">
            {chosen.map((scene) => (
              <SceneLooks
                key={scene.id}
                scene={scene}
                looks={looks[lookKey(scene)] ?? 'loading'}
                pick={picks[lookKey(scene)]}
                onPick={(pick) => setPicks((all) => ({ ...all, [lookKey(scene)]: pick }))}
                onRetry={() => askLooks(scene)}
                t={t}
              />
            ))}
          </div>
        )}

        {step === 'finish' && (
          <div className="wiz__pane wiz__finish">
            <Field label={t('wiz.quality')}>
              <Segmented
                options={['max', 'standard'] as const}
                value={engine}
                onChange={(fidelity) => set('fidelity', fidelity)}
                label={t('wiz.quality')}
                format={(fidelity) => `${t(`wiz.engine.${fidelity}`)} · ${money.cost(perPhoto(fidelity))}`}
              />
              <p className="faint">
                {t(`wiz.engineHint.${engine}`)}{' '}
                {t('scenewiz.qualityHint', { cost: money.cost(perPhoto(engine)) })}
              </p>
            </Field>

            <ApertureField settings={settings} onChange={setSettings} />

            <Field label={t('shoot.notes')} hint={t('copy.piecesHint')}>
              <textarea
                className="textarea"
                rows={2}
                value={settings.details}
                placeholder={t('shoot.notesPlaceholder')}
                onChange={(event) => set('details', event.target.value)}
              />
            </Field>
          </div>
        )}

        {step === 'go' && (
          <div className="wiz__pane wiz__recap">
            <div className="recap">
              <RecapRow label={t('scenewiz.step.model')} onEdit={() => setStep('model')} t={t}>
                {model && (
                  <span className="recap__item">
                    <Thumb assetId={portraitOf(model)} />
                    {model.name}
                  </span>
                )}
              </RecapRow>
              <RecapRow label={t('scenewiz.step.clothes')} onEdit={() => setStep('clothes')} t={t}>
                {garments.map((g) => (
                  <span key={g.id} className="recap__item">
                    <Thumb assetId={g.assetId} />
                    {g.label}
                  </span>
                ))}
              </RecapRow>
              <RecapRow label={t('scenewiz.step.scenes')} onEdit={() => setStep('scenes')} t={t}>
                {chosen.map((scene) => (
                  <span key={scene.id} className="recap__item">
                    <Thumb assetId={scene.assetId} />
                    {scene.name}
                  </span>
                ))}
              </RecapRow>
              <RecapRow label={t('scenewiz.step.outfit')} onEdit={() => setStep('outfit')} t={t}>
                {chosen.map((scene) => (
                  <span key={scene.id} className="recap__item">
                    {chosen.length > 1 && <span className="faint">{scene.name} ·</span>}
                    {lookFor(scene)?.title}
                  </span>
                ))}
              </RecapRow>
            </div>
            <div className="field">
              <label htmlFor="scene-name">{t('wiz.name')}</label>
              <input
                id="scene-name"
                className="input"
                value={name}
                placeholder={defaultName(garments, t, when.short)}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
          </div>
        )}
      </section>

      <footer className="wiz__foot">
        <button
          type="button"
          className="btn btn--ghost"
          disabled={index === 0}
          onClick={() => setStep(STEPS[Math.max(0, index - 1)]!)}
        >
          {t('wiz.back')}
        </button>
        <div className="wiz__count">
          <b>{count}</b>
          <span>
            {t(count === 1 ? 'common.frame' : 'common.frames')}
            {count > 0 && <> · {money.cost(cost)}</>}
          </span>
          <small>{blocker ?? (count > 0 ? breakdown : '')}</small>
        </div>
        <button
          type="button"
          className="btn btn--primary wiz__next"
          disabled={Boolean(blocker) || submitting}
          onClick={next}
        >
          {submitting ? <span className="spinner" /> : step === 'go' ? <SparkIcon /> : null}
          {step === 'go' ? t(submitting ? 'shoot.going' : 'copy.go') : t('wiz.next')}
          {step !== 'go' && <ArrowRightIcon />}
        </button>
      </footer>

      {creatingModel && (
        <ModelEditor
          onClose={() => setCreatingModel(false)}
          onSaved={(message) => {
            setCreatingModel(false);
            toast(message, 'ok');
            void loadModels().then((list) => {
              // The newest of the account's own is the one just made.
              const made = list.find((m) => !m.house);
              if (made) setModelId(made.id);
            });
          }}
          onChanged={() => void loadModels()}
          toast={toast}
          t={t}
        />
      )}
    </div>
  );
}

type RollLook = SceneLook | { title: string; other: string };

/**
 * One scene and the stylist's three looks for it, and room for one's own: the
 * scene's photograph beside them, as the looks are its outfit made over.
 */
function SceneLooks({
  scene,
  looks,
  pick,
  onPick,
  onRetry,
  t,
}: {
  scene: Scene;
  looks: Looks;
  pick?: LookPick;
  onPick: (pick: LookPick) => void;
  onRetry: () => void;
  t: Translate;
}) {
  const [other, setOther] = useState(pick && 'other' in pick ? pick.other : '');
  const picked = (i: number) => Boolean(pick && 'index' in pick && pick.index === i);
  const ownPicked = Boolean(pick && 'other' in pick);
  return (
    <div className="slook">
      <span className="slook__scene">
        <img src={assetUrl(scene.assetId)} alt="" loading="lazy" />
      </span>
      <div className="slook__body">
        <b className="slook__name">{scene.name}</b>
        {/* The stylist's cards, as in the chat. */}
        <div className="chat__cards slook__cards">
          {looks === 'loading' &&
            [0, 1, 2].map((i) => <span key={i} className="chat__card slook__skeleton" aria-hidden="true" />)}
          {looks === 'failed' && (
            <div className="slook__failed">
              <span className="faint">{t('scenewiz.looksFailed')}</span>
              <button type="button" className="btn btn--sm" onClick={onRetry}>
                {t('scenewiz.looksAgain')}
              </button>
            </div>
          )}
          {Array.isArray(looks) &&
            looks.map((look, i) => (
              <button
                key={i}
                type="button"
                className={`chat__card is-text${picked(i) ? ' is-on' : ''}`}
                aria-pressed={picked(i)}
                style={{ animationDelay: `${i * 70}ms` }}
                onClick={() => onPick({ index: i })}
              >
                <span className="chat__cardText">
                  <b>
                    <span className="chat__num">{i + 1}</span>
                    {look.title}
                  </b>
                  <span className="faint">{look.description}</span>
                </span>
              </button>
            ))}
          <form
            className={`chat__card chat__other${ownPicked ? ' is-on' : ''}`}
            onSubmit={(event) => {
              event.preventDefault();
              if (other.trim()) onPick({ other: other.trim() });
            }}
          >
            <b>{t('stylist.other')}</b>
            <textarea
              className="textarea"
              rows={2}
              value={other}
              placeholder={t('scenewiz.otherHint')}
              aria-label={t('scenewiz.otherHint')}
              onChange={(event) => {
                setOther(event.target.value);
                // Their own words, once picked, follow what they type.
                if (ownPicked && event.target.value.trim()) onPick({ other: event.target.value.trim() });
              }}
            />
            <button type="submit" className="btn btn--sm" disabled={!other.trim()}>
              {ownPicked ? <CheckIcon /> : null}
              {t('stylist.otherGo')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
